import React, {useEffect, useRef, useState} from 'react';
import './collabMessages.scss';
import Select from "react-select";
import Accordion from "react-bootstrap/Accordion";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import moment from "moment";
import {Loading} from "../../../../ui/loading/Loading";
import {getAuthorInitials, getAuthorKnowAs} from "../../../../../helpers/tools";
import {collabSetPanelViewRemember} from "../../../../../store/user_organizations/teams/tools/connect_and_align";

const stripHtml = (value:any) => {
    return String(value ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
};

const countComments = (comments:any[] = []): number => {
    return comments.reduce((total, comment) => {
        return total + 1 + countComments(comment?.comments || []);
    }, 0);
};

const getMessageSortValue = (message:any) => {
    return moment(message?.lastModified || message?.dateCreated || 0).valueOf();
};

const dedupeSentMessages = (messages:any[]) => {
    const seen = new Set();
    const uniqueMessages:any[] = [];

    messages.forEach((message) => {
        const key = message?.is_broadcast && message?.broadcast_group_id
            ? `broadcast:${message.broadcast_group_id}`
            : `datum:${message.id}`;

        if (seen.has(key)) {
            return;
        }

        seen.add(key);
        uniqueMessages.push(message);
    });

    return uniqueMessages;
};

const getCounterpartyLabel = (message:any, team:any, mode:'sent'|'received') => {
    if (mode === 'sent') {
        if (message?.is_broadcast === true) {
            return 'B-CAST to Team';
        }
        return `To ${getAuthorKnowAs(message?.assignedTo, team)}`;
    }

    return `From ${getAuthorKnowAs(message?.idAuthor, team)}`;
};

const getMessageSearchText = (message:any, team:any) => {
    return [
        message?.title,
        stripHtml(message?.description),
        getAuthorKnowAs(message?.idAuthor, team),
        getAuthorKnowAs(message?.assignedTo, team),
        message?.is_broadcast ? 'broadcast team' : '',
        moment(message?.dateCreated).format('DD MMM YYYY'),
    ].join(' ').toLowerCase();
};

const normalizeAccordionKeys = (eventKeys:any): string[] => {
    if (Array.isArray(eventKeys)) {
        return eventKeys.filter((key) => typeof key === 'string');
    }

    if (typeof eventKeys === 'string' && eventKeys.trim() !== '') {
        return [eventKeys];
    }

    return [];
};

const persistCollabMessagesState = (
    storageKey:string,
    currentContextKey:string,
    nextState:{
        selectedMember:any,
        searchTerm:string,
        sentOpenKeys:string[],
        receivedOpenKeys:string[],
    }
) => {
    const persistedState = {
        contextKey: currentContextKey,
        ...nextState,
    };

    sessionStorage.setItem(storageKey, JSON.stringify(persistedState));
    window.history.replaceState(
        {
            ...(window.history.state || {}),
            collabMessagesState: persistedState,
        },
        document.title,
        `${window.location.pathname}${window.location.search}`
    );
};

const getPersistedCollabMessagesState = (storageKey:string, currentContextKey:string) => {
    const historyState = window.history.state?.collabMessagesState;
    if (historyState?.contextKey === currentContextKey) {
        return historyState;
    }

    const storedStateRaw = sessionStorage.getItem(storageKey);
    if (!storedStateRaw) {
        return null;
    }

    try {
        const storedState = JSON.parse(storedStateRaw);
        if (storedState?.contextKey === currentContextKey || storedState?.contextKey === undefined) {
            return storedState;
        }
    } catch (error) {
        console.log('Unable to restore collab messages state', error);
    }

    return null;
};

const getValidMemberSelection = (selectedMember:any, members:any[] = []) => {
    const hasSelectedMember = members.some((member:any) => String(member?.id) === String(selectedMember));
    if (hasSelectedMember) {
        return selectedMember;
    }

    return members[0]?.id ?? selectedMember;
};

const MessageColumn = ({
    title,
    mode,
    messages,
    team,
    openKeys,
    setOpenKeys,
    onOpenCollab,
}: any) => {
    if (messages.length === 0) {
        return (
            <div className="collab-messages-column">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="collab-messages-column-title">{title}</div>
                    <div className="collab-message-count">0</div>
                </div>
                <div className="collab-empty-state">No messages found.</div>
            </div>
        );
    }

    return (
        <div className="collab-messages-column">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="collab-messages-column-title">{title}</div>
                <div className="collab-message-count">{messages.length}</div>
            </div>

            <Accordion
                activeKey={openKeys}
                alwaysOpen
                onSelect={(eventKeys) => setOpenKeys(normalizeAccordionKeys(eventKeys))}
            >
                {messages.map((message:any) => {
                    const commentsCount = countComments(message?.comments || []);
                    const messageStats = [
                        `${message?.uploads?.length || 0} upload${(message?.uploads?.length || 0) === 1 ? '' : 's'}`,
                        `${message?.links?.length || 0} link${(message?.links?.length || 0) === 1 ? '' : 's'}`,
                        `${commentsCount} comment${commentsCount === 1 ? '' : 's'}`,
                    ];

                    return (
                        <Accordion.Item
                            eventKey={`${mode}-${message?.broadcast_group_id || message?.id}`}
                            key={`${mode}-${message?.broadcast_group_id || message?.id}`}
                            className="collab-message-item"
                        >
                            <Accordion.Header className="collab-message-button">
                                <div className="w-100 d-flex justify-content-between align-items-start gap-3">
                                    <div>
                                        <div className="collab-message-title">{message?.title || 'Untitled message'}</div>
                                        <div className="collab-message-meta">{getCounterpartyLabel(message, team, mode)}</div>
                                        {mode === 'received' && (
                                            <div className="collab-message-meta mt-1">
                                                {`To ${getAuthorKnowAs(message?.assignedTo, team)}`}
                                            </div>
                                        )}
                                        {message?.is_broadcast === true && (
                                            <div className="mt-2">
                                                <span className="collab-message-badge">B-CAST</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="collab-message-date">
                                        {moment(message?.dateCreated).format('DD MMM YYYY')}
                                    </div>
                                </div>
                            </Accordion.Header>
                            <Accordion.Body className="collab-message-body">
                                <div
                                    className="collab-message-story"
                                    dangerouslySetInnerHTML={{__html: message?.description || '<p>No details.</p>'}}
                                />
                                <div className="collab-message-stats mt-3">
                                    {messageStats.join(' • ')}
                                </div>
                                <div className="mt-3">
                                    <button
                                        type="button"
                                        className="btn btn-teal btn-sm"
                                        onClick={() => onOpenCollab(message?.id)}
                                    >
                                        Open in Team Collab
                                    </button>
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>
                    );
                })}
            </Accordion>
        </div>
    );
};

export const CollabMessagesPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {organization_id, team_id} = useParams();
    const authenticated_user = useSelector((state:any) => state.auth);
    const cna = useSelector((state:any) => state.tool_cna);
    const team_obj = useSelector((state:any) => state.organization_team);
    const tool_cna_remember = useSelector((state:any) => state.tool_cna_remember);
    const [selectedMember, setSelectedMember] = useState<any>(authenticated_user.id);
    const [searchTerm, setSearchTerm] = useState('');
    const [sentOpenKeys, setSentOpenKeys] = useState<string[]>([]);
    const [receivedOpenKeys, setReceivedOpenKeys] = useState<string[]>([]);
    const initializedContextRef = useRef('');
    const currentContextKey = `${organization_id}-${team_id}`;
    const storageKey = `collab_messages_state_${currentContextKey}`;

    const persistPageState = ({
        nextSelectedMember = selectedMember,
        nextSearchTerm = searchTerm,
        nextSentOpenKeys = sentOpenKeys,
        nextReceivedOpenKeys = receivedOpenKeys,
    }: any = {}) => {
        persistCollabMessagesState(storageKey, currentContextKey, {
            selectedMember: nextSelectedMember,
            searchTerm: nextSearchTerm,
            sentOpenKeys: nextSentOpenKeys,
            receivedOpenKeys: nextReceivedOpenKeys,
        });
    };

    useEffect(() => {
        if (cna.isLoading === true || team_obj.isLoading === true) {
            return;
        }

        if (initializedContextRef.current === currentContextKey) {
            return;
        }

        let nextSelection:any = authenticated_user.id;
        let nextSearchTerm = '';
        let nextSentOpenKeys:string[] = [];
        let nextReceivedOpenKeys:string[] = [];
        const rememberData = tool_cna_remember?.remember?.find((org:any) => (
            org.organization_id === parseInt(String(organization_id)) &&
            org.team_id === parseInt(String(team_id)) &&
            org.open === true
        ));

        const storedState = getPersistedCollabMessagesState(storageKey, currentContextKey);
        if (storedState) {
            if (storedState?.selectedMember !== undefined && storedState?.selectedMember !== null) {
                nextSelection = storedState.selectedMember;
            }
            if (typeof storedState?.searchTerm === 'string') {
                nextSearchTerm = storedState.searchTerm;
            }
            nextSentOpenKeys = normalizeAccordionKeys(storedState?.sentOpenKeys);
            nextReceivedOpenKeys = normalizeAccordionKeys(storedState?.receivedOpenKeys);
        }

        if (rememberData?.view === 'member' && rememberData?.users?.length > 0) {
            if (!storedState) {
                nextSelection = rememberData.users[0].id;
            }
        }

        nextSelection = getValidMemberSelection(nextSelection, team_obj?.data?.members || []);

        setSelectedMember(nextSelection);
        setSearchTerm(nextSearchTerm);
        setSentOpenKeys(nextSentOpenKeys);
        setReceivedOpenKeys(nextReceivedOpenKeys);
        initializedContextRef.current = currentContextKey;
        persistCollabMessagesState(storageKey, currentContextKey, {
            selectedMember: nextSelection,
            searchTerm: nextSearchTerm,
            sentOpenKeys: nextSentOpenKeys,
            receivedOpenKeys: nextReceivedOpenKeys,
        });
    }, [authenticated_user.id, cna.isLoading, team_obj.isLoading, tool_cna_remember, currentContextKey, organization_id, team_id, storageKey]);

    useEffect(() => {
        if (initializedContextRef.current !== currentContextKey) {
            return;
        }

        persistCollabMessagesState(storageKey, currentContextKey, {
            selectedMember,
            searchTerm,
            sentOpenKeys,
            receivedOpenKeys,
        });
    }, [currentContextKey, receivedOpenKeys, searchTerm, selectedMember, sentOpenKeys, storageKey]);

    if (cna.isLoading === true || team_obj.isLoading === true) {
        return <Loading />;
    }

    const memberOptions = [
        ...(team_obj?.data?.members || []).map((member:any) => ({
            value: member.id,
            label: getAuthorKnowAs(member.id, team_obj),
            initials: getAuthorInitials(member.id, team_obj),
        }))
    ];

    const selectedMemberOption = memberOptions.find((option:any) => String(option.value) === String(selectedMember)) || memberOptions[0];
    const cnaData = Array.isArray(cna?.data) ? cna.data : [];
    const allMessages = cnaData.flatMap((memberDatum:any) => memberDatum?.messages?.data || []);
    const sortedMessages = [...allMessages].sort((left:any, right:any) => getMessageSortValue(right) - getMessageSortValue(left));

    let sentMessages = sortedMessages.filter((message:any) => Number(message?.idAuthor) === Number(selectedMember));
    sentMessages = dedupeSentMessages(sentMessages);

    let receivedMessages = sortedMessages.filter((message:any) => {
        const isReceivedBySelectedMember = Number(message?.assignedTo) === Number(selectedMember);
        const isOwnBroadcast = message?.is_broadcast === true && Number(message?.idAuthor) === Number(selectedMember);

        return isReceivedBySelectedMember && isOwnBroadcast === false;
    });

    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (normalizedSearch !== '') {
        sentMessages = sentMessages.filter((message:any) => getMessageSearchText(message, team_obj).includes(normalizedSearch));
        receivedMessages = receivedMessages.filter((message:any) => getMessageSearchText(message, team_obj).includes(normalizedSearch));
    }

    const selectMemberHandled = (option:any) => {
        const nextValue = option?.value ?? authenticated_user.id;
        setSelectedMember(nextValue);
        persistPageState({nextSelectedMember: nextValue});
        dispatch(collabSetPanelViewRemember({
            organization_id: parseInt(String(organization_id)),
            team_id: parseInt(String(team_id)),
            view: 'member',
            user_id: parseInt(String(nextValue)),
        }));
    };

    const searchTermHandled = (event:any) => {
        const nextSearchTerm = event.target.value;
        setSearchTerm(nextSearchTerm);
        persistPageState({nextSearchTerm});
    };

    const setSentOpenKeysHandled = (nextKeys:string[]) => {
        setSentOpenKeys(nextKeys);
        persistPageState({nextSentOpenKeys: nextKeys});
    };

    const setReceivedOpenKeysHandled = (nextKeys:string[]) => {
        setReceivedOpenKeys(nextKeys);
        persistPageState({nextReceivedOpenKeys: nextKeys});
    };

    const openCollabHandled = (datumId:any) => {
        persistPageState();
        navigate(`/organizations/${organization_id}/teams/${team_id}/collab/${datumId}`);
    };

    const headerLabel = `Messages for ${getAuthorKnowAs(Number(selectedMember), team_obj)}`;

    return (
        <div className="container collab-messages-page">
            <div className="row mt-3">
                <div className="col-12 col-xl-10 offset-xl-1">
                    <div className="collab-messages-toolbar">
                        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                            <div>
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm"
                                        onClick={() => navigate(`/organizations/${organization_id}/teams/${team_id}/collab`)}
                                    >
                                        Back to Team Collab
                                    </button>
                                    <div className="fw-bold text-muted">All Messages</div>
                                </div>
                                <div className="text-muted mt-1">{headerLabel}</div>
                            </div>
                            <div style={{minWidth: '240px'}}>
                                <Select
                                    value={selectedMemberOption}
                                    options={memberOptions}
                                    isSearchable={false}
                                    onChange={selectMemberHandled}
                                    formatOptionLabel={({label, initials}:any) => (
                                        <div className="d-flex align-items-center" style={{width: '170px'}}>
                                            <div
                                                className="rounded-circle"
                                                style={{backgroundColor: 'teal', height: '25px', padding: '3px', width: '26px', color: 'white', textAlign: 'center', fontSize: '12px', fontFamily: 'sans-serif'}}
                                            >
                                                {initials}
                                            </div>
                                            <div className="mx-1">{label}</div>
                                        </div>
                                    )}
                                />
                            </div>
                        </div>

                        <div className="mt-3">
                            <input
                                className="form-control collab-messages-search"
                                type="search"
                                placeholder="Search by title, person, date, or message text"
                                value={searchTerm}
                                onChange={searchTermHandled}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="row mt-3 mb-4">
                <div className="col-12 col-xl-5 offset-xl-1 mb-3 mb-xl-0">
                    <MessageColumn
                        title="Messages Sent"
                        mode="sent"
                        messages={sentMessages}
                        team={team_obj}
                        openKeys={sentOpenKeys}
                        setOpenKeys={setSentOpenKeysHandled}
                        onOpenCollab={openCollabHandled}
                    />
                </div>
                <div className="col-12 col-xl-5">
                    <MessageColumn
                        title="Messages Received"
                        mode="received"
                        messages={receivedMessages}
                        team={team_obj}
                        openKeys={receivedOpenKeys}
                        setOpenKeys={setReceivedOpenKeysHandled}
                        onOpenCollab={openCollabHandled}
                    />
                </div>
            </div>
        </div>
    );
};
